import React, { useState } from 'react';
import { Alert, Badge, Button, Form, Modal, Spinner } from 'react-bootstrap';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import { BrokerAccount } from '../types/portfolio';

interface BrokerAccountsPanelProps {
  accounts: BrokerAccount[];
  onChange: () => void | Promise<void>;
  onHoldingsMoved?: () => void | Promise<void>;
}

const PROVIDERS = [
  ['zerodha', 'Zerodha'],
  ['groww', 'Groww'],
  ['upstox', 'Upstox'],
  ['angel_one', 'Angel One'],
  ['icici_direct', 'ICICI Direct'],
  ['cdsl', 'CDSL'],
  ['nsdl', 'NSDL'],
  ['other', 'Other broker'],
];

const BrokerAccountsPanel: React.FC<BrokerAccountsPanelProps> = ({ accounts, onChange, onHoldingsMoved }) => {
  const [showForm, setShowForm] = useState(false);
  const [provider, setProvider] = useState('zerodha');
  const [displayName, setDisplayName] = useState('');
  const [maskedReference, setMaskedReference] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [moving, setMoving] = useState(false);
  const [legacyHoldings, setLegacyHoldings] = useState<{id: number; symbol: string; name: string; quantity: string}[]>([]);
  const [selectedHoldings, setSelectedHoldings] = useState<number[]>([]);
  const [destinationId, setDestinationId] = useState('');
  const [success, setSuccess] = useState('');
  const destinations = accounts.filter(account => account.is_active && !['legacy', 'manual'].includes(account.provider));

  const authHeaders = {
    Authorization: `Token ${localStorage.getItem('token') || ''}`,
  };

  const resetForm = () => {
    setProvider('zerodha');
    setDisplayName('');
    setMaskedReference('');
    setShowForm(false);
  };

  const reviewLegacyHoldings = async () => {
    setSaving(true);
    setError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/portfolio/legacy-holdings/`, { headers: authHeaders });
      setLegacyHoldings(response.data);
      setSelectedHoldings(response.data.map((holding: {id: number}) => holding.id));
      setDestinationId(destinations.length === 1 ? String(destinations[0].id) : '');
      setMoving(true);
    } catch {
      setError('Unable to load legacy holdings. Please retry.');
    } finally {
      setSaving(false);
    }
  };

  const moveHoldings = async () => {
    setSaving(true);
    setError('');
    try {
      const response = await axios.post(`${API_BASE_URL}/portfolio/legacy-holdings/`, {
        account_id: Number(destinationId), holding_ids: selectedHoldings,
      }, { headers: authHeaders });
      setMoving(false);
      setSuccess(`Moved ${response.data.moved_count} holdings to ${response.data.account_name}. Transaction history is preserved.`);
      await onChange();
      await onHoldingsMoved?.();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Unable to move holdings. Refresh and review again.');
    } finally {
      setSaving(false);
    }
  };

  const createAccount = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!displayName.trim()) {
      setError('Give this account a recognizable name.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await axios.post(`${API_BASE_URL}/broker-accounts/`, {
        provider,
        display_name: displayName.trim(),
        masked_account_ref: maskedReference.trim(),
      }, { headers: authHeaders });
      resetForm();
      await onChange();
    } catch (err: any) {
      const data = err?.response?.data;
      setError(data?.display_name?.[0] || data?.provider?.[0] || data?.error || 'Unable to add this account.');
    } finally {
      setSaving(false);
    }
  };

  const renameAccount = async (account: BrokerAccount) => {
    if (!editingName.trim()) return;
    setSaving(true);
    setError('');
    try {
      await axios.patch(`${API_BASE_URL}/broker-accounts/${account.id}/`, {
        display_name: editingName.trim(),
      }, { headers: authHeaders });
      setEditingId(null);
      await onChange();
    } catch (err: any) {
      setError(err?.response?.data?.display_name?.[0] || 'Unable to rename this account.');
    } finally {
      setSaving(false);
    }
  };

  const archiveAccount = async (account: BrokerAccount) => {
    if (!window.confirm(`Archive ${account.display_name}? Existing history will be kept.`)) return;
    setSaving(true);
    setError('');
    try {
      await axios.delete(`${API_BASE_URL}/broker-accounts/${account.id}/`, { headers: authHeaders });
      await onChange();
    } catch (err: any) {
      const blockers = err?.response?.data?.blockers;
      setError(
        blockers?.length
          ? `This account cannot be archived while it has ${blockers.join(', ')}.`
          : err?.response?.data?.error || 'Unable to archive this account.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="broker-account-panel" aria-labelledby="broker-account-title">
      <div className="broker-account-panel__header">
        <div>
          <span className="page-eyebrow">Statement ownership</span>
          <h3 id="broker-account-title">Broker and demat accounts</h3>
          <p>Add your broker label once, then reuse it for future uploads. These are separate from bank accounts in Assets. <a href="/help#imports">Import guide</a></p>
        </div>
        <Button variant={showForm ? 'outline-secondary' : 'dark'} onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Cancel' : '+ Add account'}
        </Button>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}
      {accounts.some(account => account.provider === 'legacy' && (account.current_holding_count || 0) > 0) && (
        <Alert variant="info">
          <strong>Set up ownership for your existing holdings</strong>
          <p className="mb-2">Older holdings have no recorded broker. Add their broker account above, then move the holdings below before importing more trades. Existing transactions and adjustments stay attached.</p>
          <Button size="sm" onClick={reviewLegacyHoldings} disabled={saving}>Review and move legacy holdings</Button>
        </Alert>
      )}

      {showForm && (
        <Form onSubmit={createAccount} className="broker-account-panel__form">
          <Form.Group controlId="broker-provider">
            <Form.Label>Broker or depository</Form.Label>
            <Form.Select value={provider} onChange={(event) => setProvider(event.target.value)}>
              {PROVIDERS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </Form.Select>
          </Form.Group>
          <Form.Group controlId="broker-name">
            <Form.Label>Account name</Form.Label>
            <Form.Control
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              placeholder="e.g. Zerodha - Retirement"
            />
          </Form.Group>
          <Form.Group controlId="broker-reference">
            <Form.Label>Last digits (optional)</Form.Label>
            <Form.Control
              value={maskedReference}
              onChange={(event) => setMaskedReference(event.target.value)}
              placeholder="e.g. 4603"
            />
          </Form.Group>
          <Button type="submit" variant="primary" disabled={saving}>
            {saving ? <Spinner animation="border" size="sm" /> : 'Save account'}
          </Button>
        </Form>
      )}

      <div className="broker-account-panel__grid">
        {accounts.map((account) => (
          <article className="broker-account-card" key={account.id}>
            <div className="broker-account-card__mark">{account.provider_label.slice(0, 2).toUpperCase()}</div>
            <div className="broker-account-card__body">
              {editingId === account.id ? (
                <div className="d-flex gap-2">
                  <Form.Control
                    size="sm"
                    value={editingName}
                    onChange={(event) => setEditingName(event.target.value)}
                    aria-label="Account name"
                  />
                  <Button size="sm" onClick={() => renameAccount(account)} disabled={saving}>Save</Button>
                </div>
              ) : (
                <>
                  <strong>{account.display_name}</strong>
                  <span>{account.provider_label}{account.masked_account_ref ? ` / ending ${account.masked_account_ref}` : ''}</span>
                </>
              )}
              <div className="broker-account-card__meta">
                <Badge bg={(account.current_holding_count || 0) > 0 ? 'success' : 'light'} text={(account.current_holding_count || 0) > 0 ? undefined : 'dark'}>
                  {account.current_holding_count || 0} current holding{account.current_holding_count === 1 ? '' : 's'}
                </Badge>
                {account.provider === 'legacy' && <Button variant="link" size="sm" onClick={reviewLegacyHoldings} disabled={saving}>Move holdings</Button>}
              </div>
            </div>
            {!['manual', 'legacy'].includes(account.provider) && editingId !== account.id && (
              <div className="broker-account-card__actions">
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => { setEditingId(account.id); setEditingName(account.display_name); }}
                >
                  Rename
                </Button>
                <Button variant="link" size="sm" className="text-danger" onClick={() => archiveAccount(account)}>
                  Archive
                </Button>
              </div>
            )}
          </article>
        ))}
        {accounts.length === 0 && (
          <div className="broker-account-panel__empty">
            <strong>No saved broker account yet</strong>
            <span>Add the account before importing its statement.</span>
          </div>
        )}
      </div>
      <Modal show={moving} onHide={() => { if (!saving) { setMoving(false); setError(''); } }} size="lg">
        <Modal.Header closeButton={!saving}><Modal.Title>Move legacy holdings to their broker</Modal.Title></Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <p>This changes the account label and preserves quantities, costs, transactions and adjustments. Select only holdings owned by the chosen broker. For multiple brokers, repeat for each group.</p>
          <Form.Group controlId="legacy-destination" className="mb-3">
            <Form.Label>Destination account</Form.Label>
            <Form.Select value={destinationId} onChange={event => setDestinationId(event.target.value)} disabled={saving}>
              <option value="">Choose an account</option>
              {destinations.map(account => <option key={account.id} value={account.id}>{account.display_name} ({account.provider_label})</option>)}
            </Form.Select>
            {!destinations.length && <Form.Text>Add a broker account using the form above, then return here.</Form.Text>}
          </Form.Group>
          <Form.Check label="Select all legacy holdings" checked={legacyHoldings.length > 0 && selectedHoldings.length === legacyHoldings.length} disabled={saving || !legacyHoldings.length} onChange={event => setSelectedHoldings(event.target.checked ? legacyHoldings.map(holding => holding.id) : [])} />
          <div className="mt-3" style={{maxHeight: 320, overflowY: 'auto'}}>
            {legacyHoldings.map(holding => <Form.Check key={holding.id} id={`legacy-holding-${holding.id}`} label={`${holding.symbol} — ${holding.name} (${holding.quantity} shares)`} checked={selectedHoldings.includes(holding.id)} disabled={saving} onChange={event => setSelectedHoldings(current => event.target.checked ? [...current, holding.id] : current.filter(id => id !== holding.id))} />)}
            {!legacyHoldings.length && <p>No legacy holdings remain.</p>}
          </div>
          <p className="text-muted mt-3 mb-0">Closed positions are included to preserve their history. Overlapping stocks already in the destination account require review before they can be moved.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => { setMoving(false); setError(''); }} disabled={saving}>Cancel</Button>
          <Button onClick={moveHoldings} disabled={saving || !destinationId || !selectedHoldings.length}>{saving ? 'Moving…' : `Confirm move (${selectedHoldings.length})`}</Button>
        </Modal.Footer>
      </Modal>
    </section>
  );
};

export default BrokerAccountsPanel;
